import { useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ProductCard } from "../components/Cards";
import { Meta } from "../components/Meta";
import { SearchForm } from "../components/SearchForm";
import { routes, shopCategoryPath } from "../config/routes";
import {
  catalogueBrands,
  categoryId,
  findCategory,
  matchesPriceFilter,
  sortProducts,
  visibleCategories,
  visibleProducts,
  type PriceFilter,
  type ProductSort
} from "../data/catalogue";

const PAGE_SIZE = 24;

export function ShopPage() {
  const navigate = useNavigate();
  const { categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const listings = visibleProducts();
  const liveCategories = visibleCategories();
  const brands = catalogueBrands(listings);
  const requested = categorySlug || "all";
  const activeCategory =
    requested !== "all" && liveCategories.some((item) => item.id === requested)
      ? requested
      : "all";
  const currentCategory = findCategory(activeCategory);
  const sort = (searchParams.get("sort") as ProductSort) || "name-asc";
  const price = (searchParams.get("price") as PriceFilter) || "all";
  const brand = searchParams.get("brand") || "all";
  const page = Math.max(1, Number(searchParams.get("page") || "1") || 1);

  const filtered = useMemo(() => {
    const next = listings.filter((product) => {
      const categoryMatches = activeCategory === "all" || categoryId(product) === activeCategory;
      const brandMatches = brand === "all" || product.brand === brand;
      return categoryMatches && brandMatches && matchesPriceFilter(product, price);
    });
    return sortProducts(next, sort);
  }, [activeCategory, brand, listings, price, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const pageTitle =
    activeCategory === "all"
      ? "Shop | Beautify Ourselves"
      : `${currentCategory?.name ?? "Shop"} | Beautify Ourselves`;

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all" || value === "name-asc" || value === "1") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "page") next.delete("page");
    setSearchParams(next, { replace: true });
  }

  function selectCategory(id: string) {
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    const query = next.toString();
    navigate(id === "all" ? `${routes.shop}${query ? `?${query}` : ""}` : `${shopCategoryPath(id)}${query ? `?${query}` : ""}`);
  }

  return (
    <main id="main">
      <Meta
        title={pageTitle}
        description="Browse the Beautify Ourselves preview catalogue. Preview prices may change before ordering opens."
        path={activeCategory === "all" ? routes.shop : `${routes.shop}/${activeCategory}`}
      />
      <header className="page-hero page-hero-compact">
        <div className="page-hero-inner">
          <p className="eyebrow">The collection</p>
          <h1>
            {currentCategory && activeCategory !== "all"
              ? currentCategory.name
              : "Browse the preview collection"}
          </h1>
          <p className="lead">
            {listings.length} listings are visible for browsing. Preview prices may change
            before ordering opens, and delivery is charged separately.
          </p>
          <SearchForm id="shop-search" compact />
        </div>
      </header>
      <section className="section shop-catalogue">
        <div className="section-inner">
          <div className="shop-tools">
            <div className="filters" aria-label="Filter catalogue by category">
              <button
                className="filter-button"
                type="button"
                aria-pressed={activeCategory === "all"}
                onClick={() => selectCategory("all")}
              >
                All
              </button>
              {liveCategories.map((item) => (
                <button
                  key={item.id}
                  className="filter-button"
                  type="button"
                  aria-pressed={activeCategory === item.id}
                  onClick={() => selectCategory(item.id)}
                >
                  {item.name}
                </button>
              ))}
            </div>
            <div className="filters filters-refine" aria-label="Sort and refine">
              <label className="sort-label">
                Sort
                <select
                  value={sort}
                  onChange={(event) => updateParam("sort", event.target.value)}
                >
                  <option value="name-asc">Name</option>
                  <option value="price-asc">Preview price, low to high</option>
                  <option value="price-desc">Preview price, high to low</option>
                </select>
              </label>
              <label className="sort-label">
                Brand
                <select
                  value={brand}
                  onChange={(event) => updateParam("brand", event.target.value)}
                >
                  <option value="all">All brands</option>
                  {brands.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              {(
                [
                  ["all", "All prices"],
                  ["under-20", "Under £20"],
                  ["20-40", "£20–£40"],
                  ["40-80", "£40–£80"],
                  ["80-plus", "£80 and over"]
                ] as Array<[PriceFilter, string]>
              ).map(([value, label]) => (
                <button
                  key={value}
                  className="filter-button"
                  type="button"
                  aria-pressed={price === value}
                  onClick={() => updateParam("price", value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <p aria-live="polite">
            {filtered.length} {filtered.length === 1 ? "listing" : "listings"}
            {filtered.length > PAGE_SIZE
              ? ` · page ${currentPage} of ${totalPages}`
              : ""}
          </p>
          <div className="product-grid">
            {pageItems.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
            {filtered.length === 0 && (
              <div className="empty-state">
                <h2>No matching listings</h2>
                <p>Try another category, brand or price range. We do not show discounts or ratings.</p>
              </div>
            )}
          </div>
          {totalPages > 1 && (
            <nav className="pagination" aria-label="Catalogue pages">
              <button
                type="button"
                className="filter-button"
                disabled={currentPage <= 1}
                onClick={() => updateParam("page", String(currentPage - 1))}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                className="filter-button"
                disabled={currentPage >= totalPages}
                onClick={() => updateParam("page", String(currentPage + 1))}
              >
                Next
              </button>
            </nav>
          )}
        </div>
      </section>
    </main>
  );
}
